import { AstNode } from './types';

/**
 * Render an AST back to formula text. Round-trips with parse():
 * parse(print(ast)) is structurally identical to ast — except that a CONCAT
 * nested directly inside another prints with its own &, which re-parses
 * flattened: the same text either way.
 *
 * `refToText` optionally maps stored refs (canonical keys) to editor text
 * (display names); unmapped refs print as-is.
 */
export function print(ast: AstNode, refToText?: (ref: string) => string | undefined): string {
  return printNode(ast, refToText);
}

const PRECEDENCE: Record<string, number> = {
  '=': 1,
  '<>': 1,
  '<': 1,
  '<=': 1,
  '>': 1,
  '>=': 1,
  '+': 3,
  '-': 3,
  '*': 4,
  '/': 4,
  '^': 5,
};

/** & binds looser than arithmetic and tighter than comparison, as in Excel. */
const CONCAT_PRECEDENCE = 2;

/** A CONCAT with the arguments & can spell: two or more. A one-argument CONCAT prints as a call. */
function isConcatChain(node: AstNode): boolean {
  return node.kind === 'call' && node.fn === 'CONCAT' && node.args.length >= 2;
}

function precedence(node: AstNode): number {
  switch (node.kind) {
    case 'binary':
      return PRECEDENCE[node.op];
    case 'unary':
      return 6;
    default:
      return isConcatChain(node) ? CONCAT_PRECEDENCE : 7;
  }
}

function printNode(node: AstNode, refToText?: (ref: string) => string | undefined): string {
  switch (node.kind) {
    case 'num':
      return String(node.value);
    case 'str':
      return `"${node.value.replace(/"/g, '""')}"`;
    case 'bool':
      return node.value ? 'TRUE' : 'FALSE';
    case 'field':
      return `[${refToText?.(node.ref) ?? node.ref}]`;
    case 'param':
      return node.name;
    case 'unary': {
      const operand = printNode(node.operand, refToText);
      return node.operand.kind === 'binary' ? `${node.op}(${operand})` : `${node.op}${operand}`;
    }
    case 'binary': {
      const prec = PRECEDENCE[node.op];
      const left = printNode(node.left, refToText);
      const right = printNode(node.right, refToText);
      const leftText = precedence(node.left) < prec ? `(${left})` : left;
      const rightText = precedence(node.right) <= prec ? `(${right})` : right;
      return `${leftText} ${node.op} ${rightText}`;
    }
    case 'call': {
      if (isConcatChain(node)) {
        return node.args
          .map((arg, i) => {
            const text = printNode(arg, refToText);
            const wrap = i === 0 ? precedence(arg) < CONCAT_PRECEDENCE : precedence(arg) <= CONCAT_PRECEDENCE;
            return wrap ? `(${text})` : text;
          })
          .join(' & ');
      }
      return `${node.fn}(${node.args.map((arg) => printNode(arg, refToText)).join(', ')})`;
    }
    case 'lambda':
      return `LAMBDA(${node.params.join(', ')}, ${printNode(node.body, refToText)})`;
    case 'let': {
      const bindings = node.bindings.map(({ name, value }) => `${name}, ${printNode(value, refToText)}`);
      return `LET(${[...bindings, printNode(node.body, refToText)].join(', ')})`;
    }
  }
}
